import { ArrowRight, Clock, Truck, ShieldCheck, Star, Phone, MessageCircle, Sparkles } from "lucide-react";
import { waLink } from "@/lib/whatsapp";
import { trackWhatsAppClick } from "@/lib/analytics";
import { BRAND } from "@/data/products";

export const Hero = () => {
  return (
    <section id="top" className="relative overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-[#F9F8F6] via-[#F9F8F6] to-[#F9F8F6]" />
        <div className="absolute -top-40 -right-40 w-[480px] h-[480px] rounded-full bg-[#2D4A3E]/8 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-[480px] h-[480px] rounded-full bg-[#BFA573]/10 blur-3xl" />
      </div>

      <div className="container-wide pt-10 md:pt-20 lg:pt-24 pb-16 md:pb-24">
        {/* Linha de prova social no topo do hero */}
        <div className="flex flex-wrap items-center gap-3 md:gap-4 mb-7 animate-fade-up">
          <div className="inline-flex items-center gap-2 bg-white border border-[#E5E3DB] rounded-full pl-2 pr-3.5 py-1.5">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-[#25D366] opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#25D366]" />
            </span>
            <span className="text-xs font-medium text-[#1C1C1C]">Atendentes online agora</span>
          </div>
          <div className="inline-flex items-center gap-1.5 bg-white border border-[#E5E3DB] rounded-full px-3.5 py-1.5">
            <div className="flex">
              {[0,1,2,3,4].map((i) => (
                <Star key={i} className="h-3.5 w-3.5 text-[#F5B400] fill-[#F5B400]" strokeWidth={0} />
              ))}
            </div>
            <span className="text-xs font-medium text-[#1C1C1C] ml-1">5,0 no Google</span>
          </div>
          <div className="hidden sm:inline-flex items-center gap-1.5 bg-white border border-[#E5E3DB] rounded-full px-3.5 py-1.5">
            <Sparkles className="h-3.5 w-3.5 text-[#BFA573]" strokeWidth={1.5} />
            <span className="text-xs font-medium text-[#1C1C1C]">+500 famílias atendidas em 2025</span>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <span className="overline">Coroa de Flores · Zona Sul, Zona Norte e Centro do Rio</span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#2D4A3E] mt-4">
              Coroa de Flores no Rio de Janeiro — <em className="not-italic relative inline-block">
                <span className="relative z-10 text-[#2D4A3E]">Atendimento 24 Horas</span>
                <span className="absolute inset-x-0 bottom-1 h-3 bg-[#BFA573]/30 -z-0 rounded"></span>
              </em>
            </h1>
            <p className="mt-6 text-lg text-[#5C5C5C] max-w-xl leading-relaxed">
              Aberto 24 horas. Entrega grátis em toda <strong className="text-[#2D4A3E] font-medium">Zona Sul, Zona Norte e Centro</strong> do Rio de Janeiro —
              e no <strong className="text-[#2D4A3E] font-medium">Cemitério São João Batista</strong>, Caju, Memorial do Carmo e Penitência. Faixa de homenagem grátis.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a href={waLink("Olá, preciso encomendar uma coroa de flores. Pode me ajudar?")}
                 target="_blank" rel="noreferrer"
                 data-testid="hero-whatsapp-cta"
                 onClick={() => trackWhatsAppClick("hero_cta")}
                 className="btn-whatsapp text-base shadow-[0_15px_40px_-10px_rgba(37,211,102,0.5)]">
                <MessageCircle className="h-4 w-4" /> Pedir pelo WhatsApp
              </a>
              <a href={`tel:+${BRAND.phoneE164}`} data-testid="hero-call-cta" className="btn-primary text-base">
                <Phone className="h-4 w-4" /> Ligar agora
              </a>
              <a href="#catalogo" data-testid="hero-catalog-cta"
                 className="inline-flex items-center gap-1.5 text-sm font-medium text-[#2D4A3E] hover:text-[#1F352B] px-2 py-2">
                Ver catálogo <ArrowRight className="h-3.5 w-3.5" />
              </a>
            </div>

            <p className="mt-5 text-xs text-[#5C5C5C]">
              <strong className="text-[#1C1C1C] font-medium">Pedido em até 60 segundos</strong> · PIX, cartão e dinheiro · Sem cadastro
            </p>

            <dl className="mt-10 grid grid-cols-3 gap-4 md:gap-6 max-w-lg">
              <div>
                <dt className="text-[10px] tracking-[0.2em] uppercase text-[#BFA573] mb-1">Atendimento</dt>
                <dd className="font-serif font-semibold text-3xl md:text-4xl text-[#2D4A3E] tracking-tight">24h</dd>
              </div>
              <div>
                <dt className="text-[10px] tracking-[0.2em] uppercase text-[#BFA573] mb-1">Entrega</dt>
                <dd className="font-serif font-semibold text-3xl md:text-4xl text-[#2D4A3E] tracking-tight">Grátis</dd>
              </div>
              <div>
                <dt className="text-[10px] tracking-[0.2em] uppercase text-[#BFA573] mb-1">Modelos</dt>
                <dd className="font-serif font-semibold text-3xl md:text-4xl text-[#2D4A3E] tracking-tight">25</dd>
              </div>
            </dl>
          </div>

          <div className="lg:col-span-5 relative animate-fade-up" style={{animationDelay: '120ms'}}>
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-[#E5E3DB] shadow-[0_40px_80px_-30px_rgba(45,74,62,0.45)]">
              <img
                src="/hero/capa.webp"
                alt="Coroa de flores para velório entregue em cemitério da Zona Sul do Rio de Janeiro — Botafogo, Copacabana, Ipanema"
                width="1080"
                height="782"
                fetchPriority="high"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/75 to-transparent">
                <p className="text-white/95 text-sm leading-relaxed">{BRAND.tagline}</p>
              </div>
            </div>

            <div className="hidden md:flex absolute -left-6 bottom-10 bg-white/95 backdrop-blur-xl border border-[#E5E3DB] rounded-xl p-4 gap-3 items-center shadow-[0_20px_40px_-15px_rgba(0,0,0,0.2)] w-72">
              <div className="h-11 w-11 rounded-full bg-[#2D4A3E]/10 flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="h-5 w-5 text-[#2D4A3E]" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#BFA573]">Garantia</p>
                <p className="text-sm text-[#1C1C1C] leading-snug">Foto antes da entrega</p>
              </div>
            </div>

            <div className="hidden lg:flex absolute -right-4 top-10 bg-[#2D4A3E] text-white rounded-xl px-4 py-3 gap-2 items-center shadow-[0_20px_40px_-15px_rgba(45,74,62,0.5)]">
              <Clock className="h-4 w-4 text-[#BFA573]" strokeWidth={1.5} />
              <div className="text-xs">
                <p className="font-medium">Entrega grátis</p>
                <p className="text-white/70 text-[11px]">Todo o município do RJ</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { icon: Clock, t: "Atendimento 24h", d: "Inclusive finais de semana, feriados e madrugadas — falamos com você imediatamente." },
            { icon: Truck, t: "Entrega em todos os cemitérios", d: "Atendemos todos os cemitérios e capelas do município do Rio de Janeiro." },
            { icon: ShieldCheck, t: "Faixa personalizada grátis", d: "Mensagem de homenagem inclusa em todos os modelos, sem custo adicional." },
          ].map((f) => (
            <div key={f.t} className="bg-white border border-[#E5E3DB] rounded-2xl p-6 flex items-start gap-4 hover:border-[#2D4A3E] transition-colors duration-500">
              <f.icon className="h-6 w-6 text-[#2D4A3E] flex-shrink-0 mt-1" strokeWidth={1.5} />
              <div>
                <h3 className="font-serif text-xl text-[#2D4A3E]">{f.t}</h3>
                <p className="text-sm text-[#5C5C5C] mt-1 leading-relaxed">{f.d}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
